import React, { Component, useState, useEffect, useCallback } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { wp } from "../api/WpApi";
import "../../styles/wpStyles.scss";

import data from "./_setup.json";

import PageLoading from "./PageLoading";
import { Helmet } from "react-helmet";
import parse from "html-react-parser";
import DEFAULT_THUMBNAIL from "../../resources/not-found.png";
import Pagination from "./Pagination";
import Breadcrumbs from "../helpers/Breadcrumbs";
import { removeBreadcrumbs } from "../seo/RemoveBreadcrumbs";

const CategoryList = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsperpage] = useState(10);
  const { categoryName, page } = useParams();
  const [currentpage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [totalpages, setTotalPages] = useState(null);
  const [categoryInfo, setCategoryInfo] = useState(null);

  let navigate = useNavigate();

  const getPosts = useCallback(
    (currPage) => {
      setIsLoading(true);
      wp.categories()
        .slug(categoryName)
        .then(function (cat) {
          setCategoryInfo(cat[0]);
          var catPost = cat[0];
          return wp
            .posts()
            .categories(catPost.id)
            .perPage(postsperpage)
            .page(currPage);
        })
        .then(function (posts) {
          setPosts(posts);
          setTotalPages(posts._paging.totalPages);
          setIsLoading(false);
        })
        .catch((error) => {
          setError(error);
          setIsLoading(false);
        });
    },
    [categoryName, postsperpage]
  );

  useEffect(() => {
    getPosts(currentpage);
    sessionStorage.setItem("categoryListPage", currentpage);

    return () => {
      sessionStorage.removeItem("categoryListPage");
    };
  }, [currentpage, categoryName, getPosts]);

  // Keep currentpage in sync with URL param
  useEffect(() => {
    const pageNum = page ? parseInt(page, 10) : 1;
    setCurrentPage(pageNum);
  }, [page]);

  if (error) {
    return <div className="content">
      <Helmet>
        <meta name="robots" content="noindex"></meta>
    </Helmet>
      Error: {error.message}</div>;
  } else if (isLoading) {
    return (
      <div className="container">
        <PageLoading />
      </div>
    );
  } else if (posts.length > 0) {
    const breadcrumbsItems = [
      { name: "Home", url: "/" },
      { name: "All Recipes", url: `/${data.url.slug_posts}/` },
      { name: "Categories", url: `/${data.url.slug_posts}/${data.url.slug_categories}/` },
      {
        name: categoryInfo && categoryInfo.name ? categoryInfo.name : categoryName,
        url: `/${data.url.slug_posts}/${data.url.slug_categories}/${categoryName}/`,
      },
    ];
    const filteredSchema = removeBreadcrumbs(categoryInfo.yoast_head_json.schema, process.env.REACT_APP_PUBLIC_URL);
    return (
      <div className="content container">
        <br />
        {/* {console.log(categoryInfo.yoast_head_json.schema)} */}
        <Helmet>{parse(categoryInfo.yoast_head)}</Helmet>
        <Helmet>
          <title>
            Archives of "{categoryInfo.name}" Category | Andrew's Cookin'
          </title>
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_PUBLIC_URL}/${data.url.slug_posts}/${
              data.url.slug_categories
            }/${categoryName}/${currentpage > 1 ? `page-${currentpage}` : ""}`}
          />
          <script type="application/ld+json">
            {JSON.stringify(filteredSchema)}
          </script>
        </Helmet>
        <div className="row">
          <div className="grid md:grid-cols-6 justify-between">
            <div className="md:col-span-1">
              <button
                className="btn btn-outline-secondary"
                style={{ float: "left", display: "flex" }}
                onClick={() => navigate("/recipes/category/")}
              >
                Back to Categories
              </button>
            </div>
            <div className="text-center md:col-span-4">
              <h1 className="fw-bold">
                Archives of "{categoryInfo.name}" Category
              </h1>
              <small className="fw-light">
                {parse(categoryInfo.description)}
              </small>
            </div>
          </div>
        </div>
        <Breadcrumbs items={breadcrumbsItems} />
        <hr />
        <div className="row">
          <div className="list-group col">
            {posts.map((post) => {
              let thumbnail = DEFAULT_THUMBNAIL;
              if (
                post.yoast_head_json &&
                post.yoast_head_json.og_image &&
                post.yoast_head_json.og_image.length > 0
              ) {
                const randomIdx = Math.floor(
                  Math.random() * post.yoast_head_json.og_image.length
                );
                thumbnail = post.yoast_head_json.og_image[randomIdx].url;
              }
              return (
                <Link
                  key={post.id}
                  to={`/${data.url.slug_posts}/${post.slug}/`}
                  className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300"
                  onClick={() => {
                    sessionStorage.setItem("categoryListPage", currentpage);
                  }}
                >
                  <div className="grid grid-flow-row-dense md:grid-cols-5 md:justify-items-between justify-items-center">
                    <div className="md:col-span-1">
                      <div className="post-thumbnail my-3">
                        <img
                          src={thumbnail}
                          alt={post.title.rendered}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_THUMBNAIL;
                          }}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-4">
                      <h2
                        className="fw-bold"
                        dangerouslySetInnerHTML={{
                          __html: post.title.rendered,
                        }}
                      />
                      <p
                        dangerouslySetInnerHTML={{
                          __html: post.excerpt.rendered,
                        }}
                      />
                    </div>
                  </div>
                  {/* <div className="row-span-1 grid grid-cols-2 md:grid-cols-3">
                    <div className="col-span-1 col-start-1 md:col-start-2">
                      <p>
                        <u>{readTime(post.content.rendered)}</u>
                      </p>
                    </div>
                    <div className="col-span-1 col-start-2 md:col-start-3">
                      <small>
                        {new Date(post.date).toLocaleDateString(
                          data.format.date.locale,
                          data.format.date.options
                        )}
                      </small>
                    </div>
                  </div> */}
                </Link>
              );
            })}
          </div>
        </div>
        {totalpages > 1 && (
          <div className="row">
            <div className="col d-flex justify-content-center align-items-center my-5">
              <Pagination
                currentPage={currentpage}
                totalPages={totalpages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  navigate(
                    page === 1
                      ? `/${data.url.slug_posts}/${data.url.slug_categories}/${categoryName}/`
                      : `/${data.url.slug_posts}/${data.url.slug_categories}/${categoryName}/page-${page}`
                  );
                }}
              />
            </div>
          </div>
        )}
        <hr />
      </div>
    );
  } else {
    return <div className="content">Nothing to View!</div>;
  }
};

export default CategoryList;
